/**
 * Los torneos de un club: la lista, y el formulario para crear uno.
 *
 * El formulario vive en la misma pantalla y no en otra ruta porque crear un
 * torneo es corto —nombre, fecha, precio y qué categorías corre— y porque
 * después de crearlo lo primero que se quiere ver es la lista con el nuevo
 * adentro.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import {
  SPONSOR_LIMITS,
  TOURNAMENT_LIMITS,
  enterableCategories,
  whyNotEnterable,
  type Community,
  type CommunityCategory,
  type Sponsor,
  type Tournament,
  type PaymentAccount,
} from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { BackButton } from '../components/BackButton';
import { OpenTournaments } from '../components/OpenTournaments';
import { CategoryPicker } from '../components/CategoryPicker';
import { ImagePicker } from '../components/ImagePicker';
import { AccountsBox } from '../components/AccountsBox';
import { Field } from '../components/Field';
import { MoneyField } from '../components/MoneyField';
import { useAuth } from '../auth/AuthContext';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { categoryName } from '../lib/categoryName';
import { useAnotarVisita } from '../hooks/useAnotarVisita';

export function TournamentsPage(): JSX.Element {
  // El globito del inicio baja al entrar acá. Ver `useAnotarVisita`.
  useAnotarVisita('tournaments');
  const { t } = useTranslation();
  const { session } = useAuth();
  const { id = '' } = useParams();
  /** Quién mira, para saber en cuáles torneos puede anotarse. */
  const yo = { id: session?.user.id ?? '', sex: session?.user.sex ?? null };
  const [community, setCommunity] = useState<Community | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [club, lista] = await Promise.all([api.findCommunity(id), api.listTournaments(id)]);
      setCommunity(club);
      setTournaments(lista);
    } catch (caught) {
      setError(errorMessage(t, caught));
      setTournaments([]);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && community === null) {
    return (
      <AppLayout>
        <Alert kind="error">{error}</Alert>
        <p className="links">
          <Link to="/clubes">{t('newCommunity.backToList')}</Link>
        </p>
      </AppLayout>
    );
  }

  if (community === null) {
    return (
      <AppLayout>
        <p className="help">{t('common.loading')}</p>
      </AppLayout>
    );
  }

  const puedeCrear = community.myRole === 'ADMIN' && community.canCreateTournaments;

  return (
    <AppLayout>
      <BackButton to={`/clubes/${community.id}`} />

      <div className="page-heading">
        <h1>{t('tournaments.title')}</h1>
        {puedeCrear && !creating && (
          <button type="button" onClick={() => setCreating(true)}>
            {t('tournaments.create')}
          </button>
        )}
      </div>

      <p className="lead">{t('tournaments.lead')}</p>

      {/* EL HISTORIAL VIVE APARTE Y NO ACÁ ABAJO. Esta lista es de UN club;
          el historial junta los terminados de todos, que es como se busca un
          torneo del que uno se acuerda a medias. */}
      <p className="links">
        <Link to="/torneos/historial">{t('history.title')}</Link>
      </p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/* Que el club exista no alcanza para hacer torneos: es un permiso
          aparte que da Tennis Up Club. Decirlo acá evita que alguien busque el botón
          creyendo que se le perdió. */}
      {community.myRole === 'ADMIN' && !community.canCreateTournaments && (
        <Alert kind="info">{t('tournaments.notAllowed')}</Alert>
      )}

      {community.myRole === 'ADMIN' && community.categories.length === 0 && (
        <Alert kind="info">{t('tournaments.noCategories')}</Alert>
      )}

      {creating && (
        <NewTournamentForm
          community={community}
          onCancel={() => setCreating(false)}
          onCreated={(torneo) => {
            setCreating(false);
            setTournaments((actual) => [torneo, ...(actual ?? [])]);
          }}
        />
      )}

      {tournaments === null ? (
        <p className="help">{t('common.loading')}</p>
      ) : tournaments.length === 0 ? (
        <p className="help">{t('tournaments.empty')}</p>
      ) : (
        /*
          TRES GRUPOS, Y EN ESTE ORDEN.
        
          Un torneo en borrador y uno terminado no son lo mismo con una
          etiqueta distinta: son dos cosas que se hacen en momentos
          distintos. En inscripción es donde alguien todavía puede anotarse
          —lo que la gente viene a buscar—; en curso es donde se miran
          resultados; terminados es historia. Mezclados y ordenados por
          fecha, el torneo del sábado que viene aparecía debajo de tres de
          marzo.
        
          Un grupo VACÍO NO SE DIBUJA. Un título con «no hay ninguno» debajo,
          tres veces, es una pantalla que dice tres veces que no hay nada.
        */
        <>
          {GRUPOS.map(({ status, titulo }) => {
            const delGrupo = tournaments.filter((tournament) => tournament.status === status);
            if (delGrupo.length === 0) return null;

            return (
              <section key={status}>
                <div className="section-head">
                  <h2>{t(titulo)}</h2>
                  <span className="tag">{delGrupo.length}</span>
                </div>
                <ul className="tournament-list">
                  {delGrupo.map((tournament) => (
                    <li key={tournament.id}>
                      <Link className="tournament-card" to={`/torneos/${tournament.id}`}>
                        <span className="tournament-card-name">{tournament.name}</span>
                        <span className="community-tags">
                          {/* La etiqueta de estado YA NO VA acá: el título del
                              grupo lo dice, y repetirlo en cada tarjeta era
                              decir tres veces lo mismo en la misma pantalla. */}
                          <span className="tag tag-when">
                            {t('tournaments.startsOn', { date: formatDate(tournament.startsAt) })}
                          </span>
                          {/* EL PRECIO NO ESTÁ EN LA TARJETA, y sí adentro del
                              torneo. Mirando la lista lo que se decide es
                              CUÁL abrir —cuándo es, cuánta gente hay, si me
                              puedo anotar—; el precio se pregunta después, ya
                              decidido cuál interesa, y ahí está. En la
                              tarjeta era una cuarta etiqueta que hacía
                              trabajar al ojo sin contestar esa pregunta. */}
                          <span className="tag">
                            {t('tournaments.entryCount', { n: tournament.entries.length })}
                          </span>
                          {/*
                            «PODÉS ANOTARTE», que es la pregunta con la que se
                            mira una lista de torneos. La regla completa está
                            en el contrato y es la misma que aplica la API:
                            el cuadro sin tirar, tu sexo, tu nivel, y que no
                            hayas llegado al tope de dos categorías.
                          */}
                          {/*
                            «PODÉS ANOTARTE» O POR QUÉ NO.
                            
                            Antes, no poder se veía igual que no haber mirado:
                            la etiqueta simplemente no estaba. Javier miró un
                            torneo de A siendo B —que sí puede jugar— y no
                            entendió por qué no le aparecía; el motivo era que
                            ese cuadro ya se había sorteado, y la pantalla no
                            tenía cómo decírselo.
                            
                            `whyNotEnterable` es la MISMA decisión mirada del
                            otro lado, así que nunca puede contradecir a la
                            etiqueta verde.
                          */}
                          <PuedeAnotarse tournament={tournament} yo={{ ...yo, level: tournament.myLevel }} />
                        </span>
                        <SponsorStrip sponsors={tournament.sponsors} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </>
      )}

      {/* LA VITRINA, DEBAJO Y SIN EL CLUB PROPIO. Sus torneos ya están
          arriba, con más detalle y con el botón de inscribirse; repetirlos
          acá hace dudar de si son los mismos o hay dos parecidos. */}
      <OpenTournaments exceptCommunityId={community.id} />
    </AppLayout>
  );
}

/**
 * Los grupos de la lista, en el orden en que se muestran.
 *
 * `DRAFT` primero porque es el único donde alguien todavía puede anotarse, y
 * anotarse es lo que la gente viene a hacer.
 */
const GRUPOS: ReadonlyArray<{ status: Tournament['status']; titulo: string }> = [
  { status: 'DRAFT', titulo: 'tournaments.groupOpen' },
  { status: 'RUNNING', titulo: 'tournaments.groupRunning' },
  { status: 'FINISHED', titulo: 'tournaments.groupFinished' },
];

/** `2026-09-20` tal cual, sin zona horaria: es una fecha civil, no un instante. */
export function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  if (year === undefined || month === undefined || day === undefined) return isoDate;
  return `${day}/${month}/${year}`;
}

// =====================================================================

function NewTournamentForm({
  community,
  onCreated,
  onCancel,
}: {
  community: Community;
  onCreated: (tournament: Tournament) => void;
  onCancel: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [fee, setFee] = useState<number | null>(null);
  const [secondFee, setSecondFee] = useState<number | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [categories, setCategories] = useState<CommunityCategory[]>([]);
  const [banner, setBanner] = useState<File | null>(null);
  const [sponsors, setSponsors] = useState<NuevoPatrocinador[]>([]);
  /**
   * LAS CUENTAS Y EL REGLAMENTO, AL CREAR.
   *
   * Javier, armando un torneo: «no me deja poner las cuentas bancarias, ni
   * poner el reglamento». Se podían, pero DESPUÉS de crear, en la ficha del
   * torneo — y la pantalla no lo decía. Quien arma un torneo lo arma de una
   * sentada; mandarlo a buscar dos secciones más adelante es cómo un torneo
   * queda sin cuentas hasta que alguien pregunta a dónde deposita.
   *
   * LAS CUENTAS VIVEN EN MEMORIA HASTA QUE SE CREA EL TORNEO: todavía no hay
   * torneo al que colgarlas. Por eso llevan un id inventado —`AccountsBox`
   * necesita uno para poder quitarlas de la lista— que el servidor nunca ve.
   */
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [rules, setRules] = useState('');
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listo =
    name.trim().length >= TOURNAMENT_LIMITS.nameMin && startsAt !== '' && fee !== null && categories.length > 0;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!listo) return;
    setSending(true);
    setError(null);
    try {
      /**
       * SON VARIAS LLAMADAS Y EN ESTE ORDEN. El torneo primero, con sus datos;
       * después el banner y los patrocinadores, que van como archivos y por
       * eso no caben en la misma petición.
       *
       * Si una imagen falla después de que el torneo se creó, el torneo QUEDA
       * CREADO y se avisa cuál imagen no subió. Es a propósito: deshacer la
       * creación por un logo que pesaba de más sería tirar a la basura todo
       * lo que la persona escribió, y esas imágenes se pueden agregar después
       * desde la ficha del torneo.
       */
      let creado = await api.createTournament(community.id, {
        name: name.trim(),
        ...(description.trim() === '' ? {} : { description: description.trim() }),
        startsAt,
        feeColones: fee ?? 0,
        // `null` es «lo mismo que la primera». Es distinto de cero, que
        // sería «la segunda es gratis», y por eso no se colapsan.
        secondCategoryFeeColones: secondFee,
        openToEveryone: abierto,
        categories,
        // SE MANDAN SIEMPRE, incluso vacías: una lista vacía es «este torneo
        // va sin cuentas» y NO «heredá las del anterior». Ver el servicio.
        accounts: accounts.map((c) => ({ number: c.number, description: c.description })),
        ...(rules.trim() === '' ? {} : { rules: rules.trim() }),
      });

      const fallaron: string[] = [];

      if (banner !== null) {
        setStep(t('tournaments.uploadingBanner'));
        try {
          creado = await api.setTournamentBanner(creado.id, banner);
        } catch {
          fallaron.push(t('branding.banner'));
        }
      }

      for (const sponsor of sponsors) {
        setStep(t('tournaments.uploadingSponsor', { name: sponsor.name }));
        try {
          creado = await api.addTournamentSponsor(
            creado.id,
            sponsor.name,
            sponsor.linkUrl,
            sponsor.image,
          );
        } catch {
          fallaron.push(sponsor.name);
        }
      }

      if (fallaron.length > 0) {
        setError(t('tournaments.someImagesFailed', { names: fallaron.join(', ') }));
      }
      onCreated(creado);
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
      setStep(null);
    }
  }

  return (
    <form className="card form" onSubmit={(e) => void submit(e)}>
      <h2>{t('tournaments.create')}</h2>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor="t-name">{t('tournaments.name')}</label>
        <input
          id="t-name"
          value={name}
          disabled={sending}
          maxLength={TOURNAMENT_LIMITS.nameMax}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="t-date">{t('tournaments.date')}</label>
        <span className="help" id="t-date-help">
          {t('tournaments.dateHelp')}
        </span>
        {/* `type="date"` a propósito: en el celular abre el calendario del
            sistema, que es el que la gente ya sabe usar. */}
        <input
          id="t-date"
          type="date"
          value={startsAt}
          disabled={sending}
          aria-describedby="t-date-help"
          onChange={(e) => setStartsAt(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="t-description">{t('tournaments.description')}</label>
        <textarea
          id="t-description"
          rows={3}
          value={description}
          disabled={sending}
          maxLength={TOURNAMENT_LIMITS.descriptionMax}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="fee-pair">
        <MoneyField
          id="t-fee"
          label={t('tournaments.fee')}
          help={t('tournaments.feeHelp')}
          value={fee}
          disabled={sending}
          onChange={setFee}
        />
        <MoneyField
          id="t-second-fee"
          label={`${t('tournaments.secondFee')} · ${t('common.optional')}`}
          help={t('tournaments.secondFeeHelp')}
          value={secondFee}
          disabled={sending}
          onChange={setSecondFee}
        />
      </div>

      {/* QUIÉN SE PUEDE ANOTAR, antes de elegir las categorías: cambia lo que
          significan. Con el check puesto, cada categoría queda abierta a
          cualquiera de la app y en cualquier nivel. */}
      <div className="field">
        <label className="check">
          <input
            type="checkbox"
            checked={abierto}
            disabled={sending}
            onChange={(e) => setAbierto(e.target.checked)}
          />
          <span>{t('tournaments.openToggle')}</span>
        </label>
        <p className="help">{t('tournaments.openToggleHelp')}</p>
      </div>

      <fieldset className="plain-fieldset">
        <legend>{t('tournaments.categoriesLabel')}</legend>
        <p className="help">{t('tournaments.categoriesHelp')}</p>
        <CategoryPicker
          value={categories}
          onChange={setCategories}
          disabled={sending}
          allowed={community.categories}
        />
      </fieldset>

      {/*
        A DÓNDE DEPOSITAR, al crear.

        Javier: «no me deja poner las cuentas bancarias». Se podían, pero
        después de crear el torneo. Ahora se escriben acá y viajan con él.

        SE REUSA `AccountsBox`, la misma caja de la ficha del torneo: las
        funciones que recibe no llaman al servidor —todavía no hay torneo— sino
        que mueven una lista en memoria. Una caja distinta para lo mismo sería
        el lugar donde un día una valida el largo del número y la otra no.
      */}
      <AccountsBox
        accounts={accounts}
        help={t('tournaments.accountsAtCreateHelp')}
        onAdd={async (input) => {
          setAccounts((antes) => [
            ...antes,
            // El id es para poder quitarla de la lista; el servidor nunca lo ve.
            { id: `nueva-${antes.length + 1}`, position: antes.length, ...input },
          ]);
        }}
        onRemove={async (accountId) => {
          setAccounts((antes) => antes.filter((c) => c.id !== accountId));
        }}
      />

      {/*
        EL REGLAMENTO. Vacío = el del club, que es lo normal.

        ANTES ACÁ SOLO HABÍA UN AVISO, y encima se escondía cuando el club no
        tenía reglamento escrito — justo cuando más falta hacía—. Ahora se
        puede escribir, y el aviso sigue estando para decir de dónde sale el
        que va a aplicar si esto queda en blanco.
      */}
      <fieldset className="plain-fieldset">
        <legend>{t('rules.title')}</legend>
        <p className="help">
          {community.tournamentRules !== null && community.tournamentRules.trim() !== ''
            ? t('tournaments.rulesFromClub')
            : t('tournaments.rulesNoClubOne')}
        </p>
        <Field id="t-rules" label={t('rules.title')}>
          {(control) => (
            <textarea
              {...control}
              rows={4}
              value={rules}
              maxLength={TOURNAMENT_LIMITS.rulesMax}
              disabled={sending}
              onChange={(e) => setRules(e.target.value)}
            />
          )}
        </Field>
      </fieldset>

      <fieldset className="plain-fieldset">
        <legend>{t('branding.title')}</legend>
        <p className="help">{t('branding.help')}</p>

        <ImagePicker
          id="t-banner"
          label={t('branding.banner')}
          help={t('branding.bannerHelp')}
          value={banner}
          disabled={sending}
          onChange={setBanner}
        />

        <SponsorFields
          sponsors={sponsors}
          disabled={sending}
          onChange={setSponsors}
        />
      </fieldset>

      <div className="button-row">
        <button type="submit" disabled={!listo || sending}>
          {sending ? (step ?? t('common.sending')) : t('tournaments.create')}
        </button>
        <button type="button" className="link" disabled={sending} onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>

      {categories.length > 0 && (
        <p className="help">
          {categories
            .map((c) => categoryName(t, c))
            .join(' · ')}
        </p>
      )}
    </form>
  );
}

// =====================================================================
// Patrocinadores, al crear
// =====================================================================

/** Un patrocinador todavía sin subir: vive en memoria hasta que se crea el torneo. */
interface NuevoPatrocinador {
  name: string;
  linkUrl: string;
  image: File;
}

/**
 * Agregar patrocinadores mientras se arma el torneo.
 *
 * SE ACUMULAN EN MEMORIA y se suben todos juntos DESPUÉS de crear el torneo,
 * porque hasta que el torneo no existe no hay a qué colgarlos. Por eso el
 * botón dice «Agregar a la lista» y no «Guardar»: nada se ha mandado todavía,
 * y decir lo contrario haría que alguien cierre la pantalla creyendo que ya
 * estaba.
 */
function SponsorFields({
  sponsors,
  disabled,
  onChange,
}: {
  sponsors: NuevoPatrocinador[];
  disabled: boolean;
  onChange: (sponsors: NuevoPatrocinador[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [image, setImage] = useState<File | null>(null);

  const listo = name.trim() !== '' && image !== null;

  return (
    <div className="sponsor-fields">
      <h3>{t('communities.sponsors')}</h3>

      {sponsors.length > 0 && (
        <ul className="member-list">
          {sponsors.map((sponsor, index) => (
            <li key={`${sponsor.name}-${index}`}>
              <div className="member-name">
                <strong>{sponsor.name}</strong>
                <span className="help">{sponsor.image.name}</span>
              </div>
              <button
                type="button"
                className="link"
                disabled={disabled}
                onClick={() => onChange(sponsors.filter((_, i) => i !== index))}
              >
                {t('branding.removeSponsor')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="field">
        <label htmlFor="t-sponsor-name">{t('branding.sponsorName')}</label>
        <input
          id="t-sponsor-name"
          value={name}
          disabled={disabled}
          maxLength={SPONSOR_LIMITS.nameMax}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="t-sponsor-link">{t('branding.sponsorLink')}</label>
        <span className="help" id="t-sponsor-link-help">
          {t('branding.sponsorLinkHelp')}
        </span>
        <input
          id="t-sponsor-link"
          type="url"
          value={linkUrl}
          disabled={disabled}
          maxLength={SPONSOR_LIMITS.linkMax}
          aria-describedby="t-sponsor-link-help"
          onChange={(e) => setLinkUrl(e.target.value)}
        />
      </div>

      <ImagePicker
        id="t-sponsor-image"
        label={t('branding.sponsorLogo')}
        value={image}
        disabled={disabled}
        onChange={setImage}
      />

      <div className="button-row">
        {/* `type="button"`: dentro de un formulario, un botón sin tipo ENVÍA.
            Sin esto, agregar un patrocinador crearía el torneo. */}
        <button
          type="button"
          className="link"
          disabled={disabled || !listo}
          onClick={() => {
            if (image === null) return;
            onChange([...sponsors, { name: name.trim(), linkUrl: linkUrl.trim(), image }]);
            setName('');
            setLinkUrl('');
            setImage(null);
          }}
        >
          {t('tournaments.addSponsorToList')}
        </button>
      </div>
    </div>
  );
}

/**
 * LOS PATROCINADORES DE UN TORNEO, EN CHIQUITO Y DENTRO DE SU TARJETA.
 *
 * NO ES `SponsorCarousel`, y no puede serlo: aquel dibuja un `<a>` por cada
 * patrocinador con enlace, y esto vive DENTRO del `<Link>` de la tarjeta. Un
 * enlace adentro de otro enlace no es HTML válido y los navegadores lo
 * resuelven cada uno a su manera — en la práctica, tocando el logo se abre
 * cualquiera de los dos. Acá los logos son solo imágenes: en el listado, la
 * tarjeta entera lleva al torneo, y los enlaces de verdad están en su ficha.
 *
 * `aria-hidden` porque en la tarjeta son decoración: el nombre accesible del
 * enlace ya dice el torneo, la fecha, la tarifa y cuánta gente hay, y sumarle
 * cuatro marcas lo vuelve ilegible de oír. En la ficha del torneo salen con
 * su nombre y su enlace, que es donde valen.
 *
 * Se desplaza con el dedo y NO tiene flechas: cuatro logos de 28 px no
 * justifican dos botones más en una tarjeta que ya es un enlace.
 */
function SponsorStrip({ sponsors }: { sponsors: Sponsor[] }): JSX.Element | null {
  if (sponsors.length === 0) return null;

  return (
    <span className="sponsor-strip" aria-hidden="true">
      {sponsors.map((sponsor) => (
        <img key={sponsor.id} src={assetUrl(sponsor.imagePath) ?? ''} alt="" loading="lazy" />
      ))}
    </span>
  );
}

/**
 * LA ETIQUETA DE «¿PUEDO ANOTARME?», con su respuesta sea cual sea.
 *
 * Las dos ramas salen de las DOS funciones del contrato que son la misma
 * decisión: `enterableCategories` dice en cuáles, `whyNotEnterable` dice por
 * qué en ninguna. Preguntar una y no la otra es cómo una pantalla muestra la
 * etiqueta verde y otra explica que no se puede.
 */
function PuedeAnotarse({
  tournament,
  yo,
}: {
  tournament: Parameters<typeof enterableCategories>[0];
  yo: Parameters<typeof enterableCategories>[1];
}): JSX.Element | null {
  const { t } = useTranslation();

  if (enterableCategories(tournament, yo).length > 0) {
    return <span className="tag tag-ok">{t('tournaments.canEnter')}</span>;
  }

  const motivo = whyNotEnterable(tournament, yo);
  if (motivo === null) return null;

  /*
   * «YA TERMINÓ» NO SE DICE ACÁ. El título del grupo ya dice «Terminados», y
   * repetirlo en cada tarjeta es decir dos veces lo mismo en la misma
   * pantalla — el mismo motivo por el que se sacó la etiqueta de estado.
   */
  if (motivo === 'TOURNAMENT_FINISHED') return null;

  return (
    <span className="tag tag-muted">
      {t(`cantEnterMine.${motivo}`, {
        level: yo.level === null ? '' : t(`categoryLevel.${yo.level}`),
      })}
    </span>
  );
}
